<template>
  <LanderLayout :config="config">
    <div v-if="existingTrip.id && !showAnotherTrip" class="existing">
      <h3>Important!</h3>
      <p>We have detected you have an existing trip. Please do not submit this form again unless you want to book flights for multiple programs.</p>
      <p>
        <button @click="visitExistingTrip">Visit your existing trip request here</button>
      </p>
      <p><a @click="showAnotherTrip = true">Or click here to book a flight for a second program</a>.</p>
    </div>

    <template v-else>
      <template v-if="!tripCreated">
        <Result v-model:error="error" :is-no-scroll="true" />
        <a v-if="isDev" href="#" style="float: right;" @click.prevent="devFill">Dev Fill</a>

        <LanderContactDetails v-if="config" v-model:email="formData.email" v-model:program-id="formData.programId" v-model:name="formData.name" :config="config" :current-step="stepNumbers.contactDetails" :organization-id="programFull.organization_id" @update:voucher-blocked="voucherBlocked = $event" />

        <LanderAirport v-if="config?.features?.is_airport_selection_shown" v-model:from="formData.homeAirportFrom" v-model:to="formData.homeAirportTo" v-model:rt-is-diff="formData.homeAirportRtIsDiff" v-model:is-date-diff="formData.homeAirportIsDateDiff" v-model:date-diff="formData.homeAirportDateDiff" :is-diff-dates-shown="config?.options?.is_diff_dates_shown" :current-step="stepNumbers.airport" />

        <LanderFlightType v-if="config?.features?.is_flight_type_shown && !config?.options?.is_auto_book" v-model="formData.typeIsGroup" :config="config" :current-step="stepNumbers.flightType" />

        <LanderTraveler v-if="config?.features?.is_traveler_shown" :config="config" :current-step="stepNumbers.traveler" />

        <LanderCollaborators v-if="config?.features?.is_collaborators_shown" v-model="formData.collaborators" :config="config" :current-step="stepNumbers.collaborators" />

        <LanderPayment v-if="config?.features?.is_payment_shown" v-model="formData.card" :config="config" :current-step="stepNumbers.payment" />

        <LanderInsurance v-if="config?.features?.is_insurance_shown" v-model="formData.insuranceType" :config="config" :current-step="stepNumbers.insurance" />

        <LanderVisa v-if="config?.features?.is_visa_shown" v-model="formData.visaReceived" :current-step="stepNumbers.visa" />
      </template>

      <LanderFlightSearch v-model:booking-error="bookingError" v-model:simulate="simulate" :config="config" :current-step="stepNumbers.flightSearch" :skip-self-search="skipSelfSearch" :search-params-ready="searchParamsReady" :program-full="programFull" :flight-params="flightParams" :flight-error="flightError" :cache-bust="searchCacheBust" :trip-created="tripCreated" :booking-loading="bookingLoading" @book-flight="retryBookFlight" @request-expert="requestExpert" />

      <template v-if="!tripCreated">
        <LanderDisclaimers v-if="config?.disclaimers?.length" v-model="formData.disclaimersAccepted" :disclaimers="config.disclaimers" :current-step="stepNumbers.disclaimers" />

        <LanderSubmit :is-loading="loading" :label="config?.options?.is_auto_book && formData.flightsSelected && !formData.prefersExpert ? 'Book This Flight' : 'Submit Your Request'" @submit="handleSubmit" />
      </template>
    </template>
  </LanderLayout>
</template>

<script setup>
import { onMounted, provide, computed, ref, watch, reactive, nextTick } from "vue";
import { useRoute } from "vue-router";
import useApi from "../utils/apiCalls.js";
import useCookie from "../utils/cookies.js";
import useDate from "../utils/datetime.js";
import { setCurrency } from "../utils/money.js";

// Components
import Result from "./common/FormResult.vue";
import LanderLayout from "./LanderLayout.vue";
import LanderDisclaimers from "./LanderDisclaimers.vue";
import LanderContactDetails from "./LanderContactDetails.vue";
import LanderCollaborators from "./LanderCollaborators.vue";
import LanderAirport from "./LanderAirport.vue";
import LanderFlightType from "./LanderFlightType.vue";
import LanderTraveler from "./LanderTraveler.vue";
import LanderPayment from "./LanderPayment.vue";
import LanderInsurance from "./LanderInsurance.vue";
import LanderVisa from "./LanderVisa.vue";
import LanderFlightSearch from "./LanderFlightSearch.vue";
import LanderSubmit from "./LanderSubmit.vue";

const route = useRoute();
const { apiGetRequest, apiPostRequest } = useApi();
const { getCookie, setCookie } = useCookie();
const { utilDateFlightSearchAPI } = useDate();

// Configuration from API
const config = ref(null);
const loading = ref(false);
const error = ref(null);
const flightError = ref("");
const searchCacheBust = ref(0);

// Booking phase state (after trip creation, for auto-book error recovery)
const tripId = ref(null);
const tripCreated = ref(false);
const bookingLoading = ref(false);
const bookingError = ref(null);
const isDev = import.meta.env.DEV;
const simulate = ref("");

const devFill = async () => {
  // Load programs and pick the first one (with first subcategory if applicable)
  const slug = route.params.slug;
  const data = await apiGetRequest({ path: `/events/${slug}` });
  const programs = data?.events || data || [];
  if (programs.length > 0) {
    formData.programId = programs[0].id;
  }

  formData.email = "dev@flightfox.com";
  formData.name = "Dev Tester";
  formData.homeAirportFrom = "LAX";
  formData.passportFirstName = "Dev";
  formData.passportLastName = "Tester";
  formData.passportCountry = 231; // United States
  formData.dateOfBirthYear = "2000";
  formData.dateOfBirthMonth = "6";
  formData.dateOfBirthDay = "15";
  formData.sex = "m";
  formData.phoneNumber = "+12025551234";
  formData.card = {
    number: "4242424242424242",
    security: "123",
    expiryMonth: 12,
    expiryYear: new Date().getFullYear() + 2,
    name: "Dev Tester",
    addressStreet: "123 Test St",
    addressCity: "Los Angeles",
    addressState: "CA",
    addressCountry: "United States",
    addressZip: "90001",
  };

  // Accept all disclaimers
  if (config.value?.disclaimers?.length) {
    for (const d of config.value.disclaimers) {
      formData.disclaimersAccepted[d.id] = true;
    }
  }
};

// Preserve ideal flight data across flightParams recomputations
const legIdeals = ref([{}, {}]);

// Form state
const formData = reactive({
  // Contact details
  email: "",
  name: "",
  programId: null,

  // Airport
  homeAirportFrom: null,
  homeAirportTo: null,
  homeAirportRtIsDiff: false,
  homeAirportIsDateDiff: false,
  homeAirportDateDiff: null,

  // Flight type
  typeIsGroup: "Chaperoned",

  // Traveler
  passportFirstName: null,
  passportMiddleName: null,
  passportLastName: null,
  dateOfBirthYear: "",
  dateOfBirthMonth: "",
  dateOfBirthDay: "",
  sex: "",
  phoneNumber: "",
  passportCountry: "",
  seatPreference: null,
  dietaryRequirements: null,

  // Collaborators
  collaborators: [
    { name: "", email: "", relationship: "" },
    { name: "", email: "", relationship: "" },
    { name: "", email: "", relationship: "" },
  ],

  // Payment
  card: null,

  // Other
  pax: 1,
  umName: null,
  umPhone: null,
  umAddress: null,
  visaReceived: null,
  insuranceType: null,
  disclaimersAccepted: {},

  // Flight search
  additionalRequestDetails: "",
  flightsSelected: false,
  legIndex: -1,
  searchResultsReady: false,
  flightResultsCount: 0,
  prefersExpert: false,
});

// Existing trip detection
const existingTrip = ref({ id: 0 });
const showAnotherTrip = ref(false);
const user = ref({ id: 0 });
const userIsExpert = ref(false);
const customerIP = ref(null);

// Flight search specific
const programFull = ref({});

// Voucher gating: true when the org requires a voucher and the entered email has none
const voucherBlocked = ref(false);

// Step numbers for each component
const stepNumbers = computed(() => {
  const steps = {};
  let stepNumber = 1;

  // Calculate step numbers based on component visibility
  if (config.value) {
    steps.contactDetails = stepNumber++;
  }
  if (config.value?.features?.is_airport_selection_shown) {
    steps.airport = stepNumber++;
  }
  if (config.value?.features?.is_flight_type_shown && !config.value?.options?.is_auto_book) {
    steps.flightType = stepNumber++;
  }
  if (config.value?.features?.is_traveler_shown) {
    steps.traveler = stepNumber++;
  }
  if (config.value?.features?.is_collaborators_shown) {
    steps.collaborators = stepNumber++;
  }
  if (config.value?.features?.is_payment_shown) {
    steps.payment = stepNumber++;
  }
  if (config.value?.features?.is_insurance_shown) {
    steps.insurance = stepNumber++;
  }
  if (config.value?.features?.is_visa_shown) {
    steps.visa = stepNumber++;
  }
  steps.flightSearch = stepNumber++;
  if (config.value?.disclaimers?.length) {
    steps.disclaimers = stepNumber++;
  }

  return steps;
});

// Check if user is unaccompanied minor (under 15 on departure date)
const isUM = computed(() => {
  if (!formData.dateOfBirthYear || !formData.dateOfBirthMonth || !formData.dateOfBirthDay) return false;
  if (!programFull.value?.date_start) return false;

  const birthDate = new Date(formData.dateOfBirthYear, formData.dateOfBirthMonth - 1, formData.dateOfBirthDay);
  const departureDate = new Date(programFull.value.date_start);

  let age = departureDate.getFullYear() - birthDate.getFullYear();
  const monthDiff = departureDate.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && departureDate.getDate() < birthDate.getDate())) {
    age--;
  }

  return age < 15;
});

// Check if should skip self search and why
const skipSelfSearchReason = computed(() => {
  if (!programFull.value.id) return null;
  // Open jaw (different arrive/depart airports) is now supported - removed check
  if (programFull.value.is_self_search_disabled) return "program_disabled";
  if (formData.homeAirportIsDateDiff) return "different_dates";
  if (isUM.value) return "under_age";
  return null;
});

const skipSelfSearch = computed(() => skipSelfSearchReason.value !== null);

// Reason why self-booking is not allowed (null = allowed)
const selfBookAllowedReason = computed(() => {
  if (!config.value?.options?.is_auto_book) return "config_disabled";
  if (!formData.programId) return "no_program_selected";
  if (!programFull.value.id) return "program_not_loaded";
  if (skipSelfSearchReason.value) return skipSelfSearchReason.value;
  if (!formData.passportFirstName) return "no_passport_name";
  if (!formData.homeAirportFrom) return "no_home_airport";
  if (formData.homeAirportRtIsDiff && !formData.homeAirportTo) return "chose_different_return_airport";
  return null;
});

// Check if search params ready
const searchParamsReady = computed(() => {
  if (!formData.programId) return false;
  if (!programFull.value.id) return false;
  if (!programFull.value.date_start || !programFull.value.date_end) return false;
  if (programFull.value.is_self_search_disabled) return false;
  if (!formData.passportFirstName) return false;
  if (!formData.homeAirportFrom) return false;
  if (formData.homeAirportIsDateDiff) return false;
  if (formData.homeAirportRtIsDiff && !formData.homeAirportTo) return false;
  return true;
});

// Flight parameters for search
const flightParams = computed(() => {
  // Ensure program data is loaded
  if (!programFull.value?.id) {
    return {
      route: [],
      cabin: "economy",
      adults: formData.pax || 1,
      children: 0,
      infants: 0,
    };
  }

  const params = {
    way: "roundtrip",
    route: [
      {
        // Outbound
        from: { iata: formData.homeAirportFrom },
        to: { iata: programFull.value.airport_iata_arrive || programFull.value.airport_iata_depart },
        depart: programFull.value.date_start,
        ideal: legIdeals.value[0],
        flexibility: 2,
      },
      {
        // Return
        from: { iata: programFull.value.airport_iata_depart || programFull.value.airport_iata_arrive },
        to: { iata: formData.homeAirportRtIsDiff ? formData.homeAirportTo : formData.homeAirportFrom },
        depart: programFull.value.date_end,
        ideal: legIdeals.value[1],
        flexibility: 2,
      },
    ],
    cabin: "economy",
    pax: formData.pax || 1,
    adults: formData.pax || 1,
    children: 0,
    infants: 0,
  };

  return params;
});

// API Methods
const loadLanderConfig = async slug => {
  const response = await apiGetRequest({ path: `/landers/${slug}/config` });
  return response;
};

const loadUser = () => {
  const jwt = getCookie("jwt");
  if (!jwt) return Promise.resolve({ id: 0 });
  return apiGetRequest({ path: "/users" });
};

const checkExistingTrip = (category, email) => {
  if (!email) return Promise.resolve({ id: 0 });
  return apiGetRequest({ path: `/organization-programs/${category}/is-existing/${email}` });
};

// Validation Methods
const validateEmail = email => {
  if (!email) return false;
  const regex = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return regex.test(String(email).toLowerCase());
};

const validateForm = (formData, config) => {
  const errors = [];

  // Step 1: Contact details
  if (!formData.programId) {
    errors.push("Please select a program");
  }

  if (!formData.email) {
    errors.push("Please enter an email address");
  } else if (!validateEmail(formData.email)) {
    errors.push("Please enter a valid email address");
  }

  // Step 2: Airport (if shown)
  if (config?.features?.is_airport_selection_shown && !formData.homeAirportFrom) {
    errors.push("Please enter a home airport");
  }

  // Step 3: Traveler (if shown and not sponsored)
  if (config?.features?.is_traveler_shown && formData.pax === 1) {
    if (!formData.passportFirstName || !formData.passportLastName) {
      errors.push("Please enter passport first and last name");
    }

    if (!formData.passportCountry) {
      errors.push("Please enter passport country");
    }

    if (!formData.dateOfBirthYear || !formData.dateOfBirthMonth || !formData.dateOfBirthDay) {
      errors.push("Please enter date of birth");
    }

    if (!formData.sex) {
      errors.push("Please enter gender");
    }

    if (!formData.phoneNumber || !/^\+[1-9]{1}[0-9]{7,11}$/.test(formData.phoneNumber)) {
      errors.push("Please enter a valid phone number (format: +12345678901)");
    }
  }

  // Step 4: Payment (if shown and required)
  if (config?.features?.is_payment_shown && !config?.options?.is_payment_optional) {
    if (!formData.card?.number) {
      errors.push("Please enter payment information");
    }
  }

  // Flight selection (if self-search is available and user chose to select flights)
  if (config?.options?.is_auto_book && !skipSelfSearch.value && searchParamsReady.value && !formData.prefersExpert && formData.searchResultsReady && formData.flightResultsCount > 0 && !formData.flightsSelected) {
    errors.push("Please select your flights and fare");
  }

  // Step 8/9: Disclaimers (if present)
  if (config?.disclaimers?.length) {
    // Check if all disclaimers are accepted
    const unchecked = config.disclaimers.filter(d => !formData.disclaimersAccepted[d.id]);
    if (unchecked.length > 0) {
      errors.push("Please check all review boxes");
    }
  }

  // Collaborators (validate emails if provided)
  if (config?.features?.is_collaborators_shown) {
    for (const collab of formData.collaborators) {
      if (collab.email && !validateEmail(collab.email)) {
        errors.push("Please enter a valid collaborator email address");
        break;
      }
    }
  }

  return errors;
};

// Provide shared state
provide("landerState", { config, formData });
provide("userIsExpert", userIsExpert);
provide(
  "groupFlightLabel",
  computed(() => config.value?.label_flight_type_group || "Chaperoned")
);
provide(
  "individualFlightLabel",
  computed(() => config.value?.label_flight_type_individual || "Unchaperoned")
);

// Check for existing trip when email changes
watch(
  () => formData.email,
  async newEmail => {
    if (newEmail && config.value?.category) {
      try {
        const result = await checkExistingTrip(config.value.category, newEmail);
        if (result) {
          existingTrip.value = result;
          customerIP.value = result.ip;
        }
      } catch {
        // Silently fail
      }
    }
  }
);

// Load program details when program changes
watch(
  () => formData.programId,
  async newId => {
    programFull.value = {};
    if (newId) {
      try {
        programFull.value = (await apiGetRequest({ path: `/event/${newId}` })) || {};
      } catch {
        // Silently fail
      }
    }
  }
);

// Clear stale values when user toggles airport/date options back to No
watch(
  () => formData.homeAirportRtIsDiff,
  val => {
    if (!val) {
      formData.homeAirportTo = null;
    }
  }
);

watch(
  () => formData.homeAirportIsDateDiff,
  val => {
    if (!val) {
      formData.homeAirportDateDiff = null;
    }
  }
);

// Reset flight selection when program changes
watch(
  () => formData.programId,
  () => {
    formData.flightsSelected = false;
    formData.legIndex = -1;
    formData.searchResultsReady = false;
    formData.prefersExpert = false;
    legIdeals.value = [{}, {}];
  }
);

// Reset flight search state when home airport changes
watch(
  () => formData.homeAirportFrom,
  (val, old) => {
    if (old && val) {
      formData.flightsSelected = false;
      formData.searchResultsReady = false;
      formData.flightResultsCount = 0;
      formData.legIndex = 0;
      legIdeals.value = [{}, {}];
    }
  }
);

// Auto search flights when params are ready
watch(
  () => searchParamsReady.value,
  val => {
    if (val && config.value?.options?.is_auto_book && !skipSelfSearch.value) {
      formData.legIndex = 0;
    } else {
      formData.searchResultsReady = false;
      formData.flightResultsCount = 0;
    }
  }
);

// Clear inline flight error when a new flight is selected
watch(
  () => formData.flightsSelected,
  val => {
    if (val) flightError.value = "";
  }
);

// Sync ideal flight selections into legIdeals so they survive flightParams recomputations
watch([() => flightParams.value.route?.[0]?.ideal, () => flightParams.value.route?.[1]?.ideal], ([ideal0, ideal1]) => {
  if (ideal0 && Object.keys(ideal0).length && ideal0 !== legIdeals.value[0]) legIdeals.value[0] = ideal0;
  if (ideal1 && Object.keys(ideal1).length && ideal1 !== legIdeals.value[1]) legIdeals.value[1] = ideal1;
});

// Cache warming: fire flight search in background for auto-eligible programs
const cacheWarmFired = ref(false);
watch(searchParamsReady, ready => {
  if (ready && config.value?.options?.is_auto_book && !cacheWarmFired.value) {
    cacheWarmFired.value = true;
    const fp = flightParams.value;
    if (fp?.route?.length) {
      const legs = fp.route
        .map(leg => {
          const date = leg.depart ? utilDateFlightSearchAPI(leg.depart) : "";
          const from = leg.from?.iata || "";
          const to = leg.to?.iata || "";
          return `${date}:${from}-${to}`;
        })
        .join(",");
      let url = `${import.meta.env.VITE_APP_FLIGHTFOX_API_URL}/flight-search?cabin=${fp.cabin}&pax=${fp.pax}&legs=${legs}`;
      if (programFull.value?.id) url += `&event-id=${programFull.value.id}`;
      window.fetch(url).catch(() => {}); // fire and forget, no auth needed
    }
  }
});

// Function to visit existing trip
const visitExistingTrip = () => {
  if (existingTrip.value.id) {
    window.location.href = `${import.meta.env.VITE_APP_FLIGHTFOX_APP_CUSTOMER_URL}/trips/${existingTrip.value.id}`;
  }
};

onMounted(async () => {
  loading.value = true;
  try {
    // Load lander config
    config.value = await loadLanderConfig(route.params.slug);
    setCurrency(config.value?.currency_code, config.value?.currency_rate);

    // If flight type selection is not shown, set typeIsGroup to "Individual" to show all flights
    if (!config.value?.features?.is_flight_type_shown) {
      formData.typeIsGroup = "Individual";
    }

    // Load user if logged in
    const userData = await loadUser();
    if (userData) {
      user.value = userData;
      userIsExpert.value = userData.is_expert || false;
      if (userData.email) {
        formData.email = userData.email;
      }
    }
  } catch (err) {
    // Redirect to home if lander not found
    if (err.response?.status === 400) {
      window.location.href = "/";
      return;
    }
    error.value = err;
  } finally {
    loading.value = false;
  }
});

const handleSubmit = async () => {
  const errors = validateForm(formData, config.value);
  if (errors.length > 0) {
    error.value = Error(errors.join("<br />"));
    return;
  }

  // Org requires a voucher and this email has none: block submission
  if (voucherBlocked.value) {
    error.value = Error("This email is NOT linked to a valid voucher. Please use a different email to continue.");
    return;
  }

  loading.value = true;
  error.value = null;
  flightError.value = "";

  const payload = {
    ip_address: customerIP.value,
    category: config.value.category,
    email: formData.email,
    name: formData.name || `${formData.passportFirstName} ${formData.passportLastName}`,
    organization_id: programFull.value.organization_id || null,
    organization_program_id: formData.programId,
    organization_program_type: formData.typeIsGroup,
    home_airport_from: formData.homeAirportFrom || "Gateway",
    home_airport_to: (formData.homeAirportRtIsDiff ? formData.homeAirportTo : formData.homeAirportFrom) || "Gateway",
    home_airport_dates_diff: formData.homeAirportIsDateDiff ? formData.homeAirportDateDiff : null,
    pax_count: formData.pax,
    um_name: formData.umName,
    um_phone: formData.umPhone,
    um_address: formData.umAddress,
    collaborators: formData.collaborators,
    self_book_allowed: !skipSelfSearch.value && searchParamsReady.value && config.value?.options?.is_auto_book,
    self_book_allowed_reason: selfBookAllowedReason.value,
    self_book_concierge_chosen: !skipSelfSearch.value && searchParamsReady.value && config.value?.options?.is_auto_book && formData.prefersExpert,
    self_book_flight_results_count: formData.flightResultsCount ?? 0,
  };

  if (formData.passportFirstName) {
    payload.traveler = {
      passport_first_name: formData.passportFirstName,
      passport_middle_name: formData.passportMiddleName,
      passport_last_name: formData.passportLastName,
      passport_number: formData.passportNumber,
      passport_country_id: formData.passportCountry || null,
      date_of_birth: formData.dateOfBirthYear ? new Date(formData.dateOfBirthYear, formData.dateOfBirthMonth - 1, formData.dateOfBirthDay) : null,
      sex: formData.sex,
      phone_number: formData.phoneNumber,
      dietary_requirements: formData.dietaryRequirements,
      email: formData.email,
      seat_preference: formData.seatPreference,
      is_me: true,
    };
  }

  if (formData.card) {
    payload.card = {
      number: formData.card.number ? formData.card.number.toString() : null,
      cvc: formData.card.security ? formData.card.security.toString() : null,
      expiry_month: formData.card.expiryMonth ? formData.card.expiryMonth.toString() : null,
      expiry_year: formData.card.expiryYear ? formData.card.expiryYear.toString() : null,
      full_name: formData.card.name,
      street: formData.card.addressStreet,
      city: formData.card.addressCity,
      state: formData.card.addressState,
      label: "Participant card",
      country: formData.card.addressCountry,
      zip_code: formData.card.addressZip,
    };
  }

  // Add additional flight preferences if provided (when self-search not used)
  if (formData.additionalRequestDetails && formData.additionalRequestDetails.trim()) {
    if (!payload.description) {
      payload.description = "\r\n\r\n***\r\n\r\nFLIGHTS\r\n\r\n";
    }
    payload.description += formData.additionalRequestDetails.trim();
  }

  // Add visa and insurance info to description
  if (!payload.description) payload.description = "";

  if (config.value?.features?.is_insurance_shown && formData.insuranceType) {
    payload.description += "\r\n\r\nInsurance Type: " + formData.insuranceType;
  }

  if (config.value?.features?.is_visa_shown && formData.visaReceived !== null) {
    payload.description += "\r\nVisa Received: " + (formData.visaReceived === "eta" ? "ETA Only Required" : formData.visaReceived ? "Yes" : "No");
  }

  // Wrap in a Promise so we can properly await the result and guarantee loading stops.
  // apiPostRequest swallows errors into onError callbacks, so we use resolve/reject to
  // surface them to a standard try/catch/finally block.
  let responseData = null;
  let isFlightError = false;

  try {
    responseData = await new Promise((resolve, reject) => {
      apiPostRequest({
        path: "/landers/trip",
        data: payload,
        onSuccess: resolve,
        onError: reject,
      });
    });
  } catch (err) {
    loading.value = false;
    const raw = err?.response?.data?.toString() || err?.message || "";
    const lower = raw.toLowerCase();

    if (lower.includes("no longer available") || lower.includes("price") && (lower.includes("changed") || lower.includes("increased") || lower.includes("exceeds"))) {
      flightError.value = raw;
      isFlightError = true;
    } else {
      error.value = err;
    }

    if (isFlightError) {
      formData.flightsSelected = false;
      formData.searchResultsReady = false;
      formData.flightResultsCount = 0;
      formData.legIndex = 0;
      legIdeals.value = [{}, {}];
      searchCacheBust.value++;
    }
  }

  if (isFlightError) {
    scrollToFlightSection();
    return;
  }

  if (responseData) {
    setCookie("jwt", responseData.token);
    tripId.value = responseData.trip_id;

    const tripUrl = `${import.meta.env.VITE_APP_FLIGHTFOX_APP_CUSTOMER_URL}/trips/${responseData.trip_id}`;

    // Auto-book with flights selected: book immediately
    if (config.value?.options?.is_auto_book && formData.flightsSelected && !formData.prefersExpert) {
      let bookResult;
      try {
        bookResult = await bookFlight();
      } catch {
        bookResult = "retry";
      }
      if (bookResult === "retry") {
        // Booking failed with retryable error, enter flight-only mode
        loading.value = false;
        tripCreated.value = true;
        scrollToFlightSection();
        return;
      }
      // Success or fatal: redirect to trip
      window.location.href = tripUrl;
      return;
    }

    // Auto-book with expert preference: post assisted, then redirect
    if (config.value?.options?.is_auto_book && formData.prefersExpert) {
      await apiPostRequest({
        path: `/trips/${responseData.trip_id}/flights/book`,
        data: {
          assisted: true,
          flight_type: formData.typeIsGroup,
          preferences: formData.additionalRequestDetails || undefined,
          flight_results_count: formData.flightResultsCount ?? 0,
          selected: formData.legIndex === 0 ? "none" : "outbound",
        },
      });
      window.location.href = tripUrl;
      return;
    }

    // Non-auto-book or no flights: redirect to trip
    window.location.href = tripUrl;
  }
};

const scrollToFlightSection = async () => {
  await nextTick();
  document.getElementById("ideal_flights")?.scrollIntoView({ behavior: "smooth", block: "start" });
};

const resetToSearch = () => {
  formData.flightsSelected = false;
  formData.legIndex = 0;
  formData.searchResultsReady = false;
  formData.flightResultsCount = 0;
  legIdeals.value = [{}, {}];
  searchCacheBust.value++;
};

const requestExpert = async () => {
  bookingLoading.value = true;
  bookingError.value = null;
  await apiPostRequest({
    path: `/trips/${tripId.value}/flights/book`,
    data: {
      assisted: true,
      flight_type: formData.typeIsGroup,
      preferences: formData.additionalRequestDetails || undefined,
      flight_results_count: formData.flightResultsCount ?? 0,
      selected: formData.legIndex === 0 ? "none" : "outbound",
    },
  });
  window.location.href = `${import.meta.env.VITE_APP_FLIGHTFOX_APP_CUSTOMER_URL}/trips/${tripId.value}`;
};

const retryBookFlight = async () => {
  const result = await bookFlight();
  if (result === "success") {
    window.location.href = `${import.meta.env.VITE_APP_FLIGHTFOX_APP_CUSTOMER_URL}/trips/${tripId.value}`;
  } else {
    scrollToFlightSection();
  }
};

const bookFlight = async () => {
  if (!flightParams.value?.route) return "retry";
  const lastLeg = flightParams.value.route[flightParams.value.route.length - 1];
  const ideal = lastLeg?.ideal;
  if (!ideal?.price_selected) { bookingError.value = "No flight selected."; return "retry"; }

  const ps = ideal.price_selected;
  const isDf = !!ps.df_offer_id;
  const isDtt = !!ps.dtt_offer_id;
  bookingLoading.value = true;
  bookingError.value = null;

  const bookingPost = data => {
    return new Promise((resolve, reject) => {
      apiPostRequest({
        path: `/trips/${tripId.value}/flights/book`,
        data,
        onSuccess: resolve,
        onError: reject,
      });
    });
  };

  // Non-bookable flight: escalate to assisted
  if (!isDf && !isDtt) {
    try {
      const legSummaries = flightParams.value.route.map(leg => {
        const legs = leg.ideal?.legs || [];
        return legs.map(l => `${l.airline_iata || ""}${l.number || ""} ${l.depart_airport || ""}-${l.arrive_airport || ""}`).join(", ");
      }).filter(Boolean).join(" / ");
      const flightNote = `Customer selected a flight not available for auto-booking ($${ps.amount}, ${ps.fare || "unknown fare"}): ${legSummaries}`;
      await bookingPost({ assisted: true, preferences: flightNote, flight_results_count: formData.flightResultsCount ?? 0, selected: "all" });
      return "success";
    } catch (err) {
      bookingError.value = err?.response?.data?.toString() || "Request failed.";
      bookingLoading.value = false;
      return "retry";
    }
  }

  // Collect disclaimers
  const allDisclaimers = flightParams.value.route
    .map(leg => leg.ideal)
    .filter(f => f?.disclaimers?.length > 0)
    .flatMap(f => f.disclaimers);
  const uniqueDisclaimers = Array.from(new Map(allDisclaimers.map(d => [d.title, d])).values());

  try {
    const result = await bookingPost({
      df_offer_id: isDf ? ps.df_offer_id : undefined,
      df_pax_ids: isDf ? ps.df_pax_ids : undefined,
      dtt_offer_id: isDtt ? ps.dtt_offer_id : undefined,
      amount: ps.amount,
      fare: ps.fare,
      simulate: simulate.value || undefined,
      disclaimers: uniqueDisclaimers,
      flight_results_count: formData.flightResultsCount ?? 0,
      selected: "all",
      // Robust fallback booking: send the channel and the selected flight's legs so the
      // API builds a hidden pending itinerary and books the full offer collection for
      // these exact flights (cheapest-within-buffer across every provider), not just the
      // one clicked offer. source="lander" activates that path (TripBookFlightLander).
      source: "lander",
      legs: flightParams.value.route.map(leg => leg.ideal?.legs || []).flat(),
    });

    const status = result?.status;

    // Success or fatal: redirect
    if (status === "success" || status === "success_pending" || status === "success_approval" || status === "failure_fatal_escalate") {
      return "success";
    }

    // Price increased: update source prices and re-render fare summary
    if (status === "failure_retry_price_increased" && result.new_price) {
      const oldAmount = ps.amount;
      flightParams.value.route.forEach(leg => {
        if (leg.ideal?.prices) {
          leg.ideal.prices.forEach(p => {
            if (p.amount === oldAmount) {
              p.amount = result.new_price;
            }
          });
        }
      });
      formData.flightsSelected = false;
      bookingError.value = result.message;
      bookingLoading.value = false;
      await nextTick();
      formData.flightsSelected = true;
      await nextTick();
      return "retry";
    }

    // Card declined: keep flights selected, show error, customer retries same booking
    if (status === "failure_retry_card_declined") {
      bookingError.value = result.message;
      bookingLoading.value = false;
      return "retry";
    }

    // Offer expired: reset search since that offer is gone
    if (status === "failure_retry_offer_expired") {
      bookingError.value = result.message;
      bookingLoading.value = false;
      resetToSearch();
      return "retry";
    }

    // Fallback: treat as success (redirect)
    return "success";
  } catch (err) {
    bookingError.value = err?.response?.data?.toString() || "Booking failed.";
    bookingLoading.value = false;
    resetToSearch();
    return "retry";
  }
};
</script>
